import { isDevMode } from '@angular/core';
import { FormControlStatus } from '@angular/forms';
import {
  AnyOffshoreSectionState,
  CalculatedValueWrite,
  OffshoreFormConfig,
} from '@navstation-portal/reporting/data-model/offshore-report';
import { GroupedFormData } from '@navstation-portal/reporting/data-model/ui';
import { FormState, InitMode, StateFieldType } from '@navstation-portal/reporting/data-model/vessel-report';
import { authEvents } from '@navstation-portal/shared/data-access/auth';
import { signalStoreFeature } from '@ngrx/signals';
import { on, withReducer } from '@ngrx/signals/events';
import { FormStoreEventGroup } from './create-form-store-events';
import { isStateField } from './offshore-vm-builder-helpers';

const updateProperty = (
  state: FormState,
  sectionId: string,
  fieldId: string,
  property: string,
  value: StateFieldType,
  index: number | undefined,
  nestedFieldId?: string | undefined
) => {
  const section = state?.[sectionId];
  if (!section) return {};

  const entry = section[fieldId];
  if (!entry) return {};
  if (isStateField(entry)) {
    if (value === entry[property]) return {};

    return updateStateFieldProperty(state, sectionId, fieldId, property, value as boolean);
  }

  if (index != null && property != null && nestedFieldId != null) {
    const indexedEntry = entry[index];
    if (!indexedEntry) return {};

    const nestedEntry = indexedEntry[nestedFieldId];
    if (!nestedEntry) return {};
    if (value === nestedEntry[property]) return {};

    return updateNestedProperty(state, sectionId, fieldId, property, value as boolean, index, nestedFieldId);
  }

  return {};
};

const updateStateFieldProperty = (
  state: FormState,
  sectionId: string,
  fieldId: string,
  property: string,
  value: StateFieldType
) => {
  const section = state?.[sectionId];
  if (!section) return {};

  const field = section[fieldId];
  if (!field) return {};

  return {
    ...state,
    [sectionId]: {
      ...section,
      [fieldId]: {
        ...field,
        [property]: value,
      },
    },
  };
};

const updateNestedProperty = (
  state: FormState,
  sectionId: string,
  fieldId: string,
  property: string,
  value: StateFieldType,
  index: number | undefined,
  nestedFieldId: string | undefined
) => {
  if (index == null || !nestedFieldId) return {};

  const section = state?.[sectionId];
  if (!section) return state;

  const fieldArray = section[fieldId];
  if (!Array.isArray(fieldArray)) return state;

  const field = fieldArray[index];
  if (!field) return state;

  const nestedField = field[nestedFieldId];
  if (!nestedField) return state;

  // When fuel type is cleared, bunker row fields become disabled. Some controls can still
  // emit stale value events during that transition; ignore those writes so cleared values stay cleared.
  const isBunkerDependentValueUpdate =
    sectionId === 'fuelOilBunkerTable' &&
    fieldId === 'bunkerRows' &&
    property === 'value' &&
    nestedFieldId !== 'fuelType';

  if (isBunkerDependentValueUpdate) {
    const fuelTypeField = field['fuelType'];
    const fuelTypeValue = fuelTypeField && 'value' in fuelTypeField ? fuelTypeField.value : undefined;

    if (!fuelTypeValue && value != null) {
      return state;
    }
  }

  // Guard against transient empty control events that can wipe auto-filled
  // bunker density right after fuel type selection.
  const isBunkerDensityValueUpdate =
    sectionId === 'fuelOilBunkerTable' &&
    fieldId === 'bunkerRows' &&
    nestedFieldId === 'density' &&
    property === 'value';

  if (
    isBunkerDensityValueUpdate &&
    (value === undefined || value === null) &&
    nestedField.value != null &&
    nestedField.touched === false
  ) {
    return state;
  }

  const updatedNestedField = {
    ...nestedField,
    [property]: value,
  };

  return {
    ...state,
    [sectionId]: {
      ...section,
      [fieldId]: [
        ...fieldArray.slice(0, index),
        {
          ...field,
          [nestedFieldId]: updatedNestedField,
        },
        ...fieldArray.slice(index + 1),
      ],
    },
  };
};

// True when the form state holds a field at this path. A row section names the
// row with `index` and the field in the row with `property`.
const holdsField = (state: FormState, { sectionId, fieldId, index, property }: CalculatedValueWrite) => {
  const entry = state?.[sectionId]?.[fieldId];
  if (isStateField(entry)) return true;
  if (!Array.isArray(entry) || index == null || property == null) return false;
  return entry[index]?.[property] != null;
};

const writeCalculatedValue = (state: FormState, write: CalculatedValueWrite): FormState => {
  const { sectionId, fieldId, value, index, property } = write;
  // updateProperty drops a write to a path that it does not find, with no
  // message. The Vessel Report copy warns in the same way.
  if (!holdsField(state, write)) {
    if (isDevMode()) {
      const row = index == null ? '' : `[${index}].${property}`;
      console.warn(`calculatedValuesChanged: the store holds no field at ${sectionId}.${fieldId}${row}`);
    }
    return state;
  }

  return { ...state, ...updateProperty(state, sectionId, fieldId, 'value', value, index, property) };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function withOffshoreFormStoreReducer<_>(
  events: FormStoreEventGroup,
  initReducer?: (
    initialData: AnyOffshoreSectionState,
    initMode: InitMode,
    formFieldRules?: Record<string, GroupedFormData>,
    formConfig?: OffshoreFormConfig
  ) => Partial<NoInfer<FormState>>,
  resetState: (() => Partial<FormState>) | Partial<FormState> = {}
) {
  const resolveResetState = () => (typeof resetState === 'function' ? resetState() : resetState);

  return signalStoreFeature(
    withReducer(
      on(events.initialiseFormState, ({ payload: { initialData, initMode, formFieldRules, formConfig } }) => () => {
        if (!initialData || !initReducer) return {};

        return initReducer(initialData, initMode, formFieldRules, formConfig as OffshoreFormConfig);
      }),
      on(events.updateFormStateTouched, ({ payload: { sectionId, fieldId, value, index, property } }) => (state) => {
        return updateProperty(state as FormState, sectionId, fieldId, 'touched', value as boolean, index, property);
      }),
      on(events.updateFormStatePristine, ({ payload: { sectionId, fieldId, value, index, property } }) => (state) => {
        return updateProperty(state as FormState, sectionId, fieldId, 'pristine', value as boolean, index, property);
      }),
      on(events.updateFormStateStatus, ({ payload: { sectionId, fieldId, value, index, property } }) => (state) => {
        return updateProperty(
          state as FormState,
          sectionId,
          fieldId,
          'status',
          value as FormControlStatus,
          index,
          property
        );
      }),
      on(events.updateFormStateValue, ({ payload: { sectionId, fieldId, value, index, property } }) => (state) => {
        return updateProperty(
          state as FormState,
          sectionId,
          fieldId,
          'value',
          value as StateFieldType,
          index,
          property
        );
      }),
      // This case sets the values and does not change a status. Offshore
      // Report has no Calculated Values yet.
      on(events.calculatedValuesChanged, ({ payload: { writes } }) => (state) => {
        return writes.reduce<FormState>(writeCalculatedValue, state as FormState);
      }),
      on(authEvents.logoutSuccess, () => resolveResetState())
    )
  );
}
